import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock3, Loader2, PackageCheck, RefreshCw, Truck } from "lucide-react";

import { GetOrderTracking } from "../api/auth";
import { useToast } from "../context/ToastContext";

const statusLabels = {
  unknown: "Awaiting delivery",
  created: "Created",
  in_transit: "In transit",
  delivered: "Delivered",
  returned: "Returned",
  cancelled: "Cancelled",
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Not recorded";

export default function TrackingPage() {
  const toast = useToast();
  const [trackings, setTrackings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTracking = async () => {
    setLoading(true);
    try {
      const response = await GetOrderTracking();
      setTrackings(response.data?.results || []);
    } catch (error) {
      setTrackings([]);
      toast.error(error.response?.data?.detail || "Unable to load order tracking.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metrics = useMemo(() => {
    const confirmed = trackings.filter((item) => item.confirmed_at).length;
    const delivered = trackings.filter((item) => item.delivery_status === "delivered").length;
    return [
      { label: "Assigned orders", value: trackings.length, icon: Clock3 },
      { label: "Confirmed", value: confirmed, icon: CheckCircle2 },
      { label: "Delivered", value: delivered, icon: PackageCheck },
      { label: "Delivery rate", value: confirmed ? `${Math.round((delivered / confirmed) * 100)}%` : "-", icon: Truck },
    ];
  }, [trackings]);

  return (
    <div className="page-shell space-y-6 px-1 py-2">
      <header className="page-header-card flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow">Operations</p>
          <h1 className="mt-3">Order tracking</h1>
        </div>
        <button type="button" className="btn-secondary" onClick={loadTracking} disabled={loading}>
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(({ label, value, icon: Icon }) => (
          <div key={label} className="metric-card">
            <Icon size={17} className="tone-accent" />
            <p className="mt-4 text-2xl font-semibold text-app-strong">{value}</p>
            <p className="mt-1 text-sm text-app-muted">{label}</p>
          </div>
        ))}
      </section>

      <section className="surface-card p-0">
        {loading ? (
          <div className="empty-state m-5 gap-2"><Loader2 size={18} className="animate-spin" /> Loading tracking...</div>
        ) : !trackings.length ? (
          <div className="empty-state m-5">Tracking will appear after the first assigned order is received.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="teqa-table">
              <thead>
                <tr><th>Order</th><th>Received</th><th>Confirmed</th><th>Delivery</th><th>Commission</th></tr>
              </thead>
              <tbody>
                {trackings.map((item) => (
                  <tr key={item.order_id}>
                    <td><span className="font-medium">#{String(item.order_id).slice(0, 8)}</span></td>
                    <td>{formatDate(item.order_created_at)}</td>
                    <td>{formatDate(item.confirmed_at)}</td>
                    <td><span className={`badge ${item.delivery_status === "delivered" ? "badge--confirmed" : item.delivery_status === "cancelled" || item.delivery_status === "returned" ? "badge--cancelled" : "badge--pending"}`}>{statusLabels[item.delivery_status] || item.delivery_status}</span></td>
                    <td>{item.commission_id ? <span className="badge badge--active">Created</span> : <span className="text-app-muted">Pending</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
